import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/app/common/common-components/ui/Dialog";
import { Button } from "@/app/common/common-components/ui/Button";
import { Loader, Search } from "lucide-react";
import apiAgent from "@/app/api/apiAgent";
import { toast } from "react-toastify";
import { SearchResultModel } from "@/app/models/SearchResultModel";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { Link } from "react-router";
import { useUserLocalPreferences } from "@/app/hooks/useUserLocalPreferences";

interface SearchDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const SEARCH_TYPES = [
  { label: "Listings", value: 0 },
  { label: "Contacts", value: 1 },
  { label: "Documents", value: 2 },
  { label: "Deals", value: 3 },
];

function hasData(obj: any): obj is { data: SearchResultModel[] } {
  return obj && Array.isArray(obj.data);
}

const SearchDialog: React.FC<SearchDialogProps> = ({ isOpen, onOpenChange }) => {
  const [search, setSearch] = useState("");
  const { preferences, updatePreference } = useUserLocalPreferences();
  const [searchType, setSearchType] = useState(preferences.lastSearchType);
  const debouncedSearch = useDebounce(search, 400);

  const {
    data: searchData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["search", debouncedSearch, searchType],
    queryFn: async () => {
      if (!debouncedSearch) return { data: [] };
      return await apiAgent.Home.searchAjax(debouncedSearch, searchType);
    },
    enabled: isOpen && !!debouncedSearch,
    staleTime: 0,
  });

  React.useEffect(() => {
    if (isError && error) {
      toast.error((error as Error).message || "Failed to search");
    }
  }, [isError, error]);

  React.useEffect(() => {
    if (!isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  const results = hasData(searchData) ? searchData.data : [];

  const handleSearchTypeChange = (value: number) => {
    setSearchType(value);
    updatePreference("lastSearchType", value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="tw-sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle className="tw-flex tw-items-center tw-gap-2">
            <Search className="tw-h-5 tw-w-5 tw-text-primary" />
            Search
          </DialogTitle>
        </DialogHeader>
        <div className="tw-mb-4 tw-flex tw-gap-2">
          <select
            className="tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-px-2 tw-py-1 tw-text-sm tw-outline-none focus:tw-border-primary"
            value={searchType}
            onChange={(e) => handleSearchTypeChange(Number(e.target.value))}
          >
            {SEARCH_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-px-2 tw-py-1 tw-text-sm tw-outline-none focus:tw-border-primary"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="tw-max-h-60 tw-overflow-y-auto">
          {isLoading ? (
            <div className="tw-flex tw-justify-center tw-py-8">
              <Loader className="tw-h-6 tw-w-6 tw-animate-spin tw-text-gray-400" />
            </div>
          ) : isError ? (
            <p className="tw-py-4 tw-text-center tw-text-red-500">{(error as Error)?.message || "Failed to search"}</p>
          ) : results.length > 0 ? (
            <ul className="tw-divide-y tw-divide-gray-200">
              {results.map((item: SearchResultModel) => (
                <li key={item.id} className="tw-py-2">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-gray-50">
                    <i className={`fa ${item.typeIcon} tw-text-gray-500`}></i>
                    <div>
                      <Link to={item.url} className="tw-text-sm tw-font-medium tw-text-gray-900 hover:tw-text-primary" onClick={() => onOpenChange(false)}>
                        {item.name}
                      </Link>
                      <span className="tw-text-xs tw-text-gray-500">
                        {item.parentName && (
                          <>
                            {" in "}
                            <Link to={item.parentUrl} className="hover:tw-text-primary" onClick={() => onOpenChange(false)}>
                              {item.parentName}
                            </Link>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : debouncedSearch ? (
            <p className="tw-py-4 tw-text-center tw-text-gray-500">No results found</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
